import { Component, effect, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CmsProductItem } from '../../../services/cms-data.service';
import { SaasProductsService } from '../../../services/saas-products.service';
import { LanguageService } from '../../../services/language.service';
import {
  CORE_CATEGORY_RE,
  VERTICAL_CATEGORY_RE,
  MODULE_PRICE,
} from '../../../services/saas-pricing';
import { HexIconComponent } from '../../shared/hex-icon/hex-icon.component';

interface BaseItem {
  label: string;
  color: string;
}

interface CatalogItem {
  title: string;
  desc: string;
  price: string;
  unit: string;
  color: string;
  /** Complemento consumible: "Incluye N {unidad}" (capacidad). */
  capacity: string | null;
}

// Reparto del catálogo CMS: Núcleo → base incluida · Vertical/packs → NO en la
// home · el resto se separa por PRECIO (>= MODULE_PRICE → módulo; por debajo →
// complemento). Ver saas-pricing.MODULE_PRICE.

/**
 * Catálogo de la home en 2 niveles (diseño "Híbrido v2"), servido desde el CMS
 * del SaaS (type_key='modulos', vía SaasProductsService): la base incluida
 * (Núcleo) + los módulos (funcionan solos) + los complementos (cuelgan de un
 * módulo). Los precios salen del CMS (campo `location`); el color de cada pieza
 * se mapea desde la paleta --ts-mod-* por palabra clave del título.
 */
@Component({
  selector: 'app-home-modulos',
  standalone: true,
  imports: [RouterLink, HexIconComponent],
  templateUrl: './home-modulos.component.html',
  styleUrl: './home-modulos.component.scss',
})
export class HomeModulosComponent {
  private saas = inject(SaasProductsService);
  private langService = inject(LanguageService);

  isLoading = signal(true);
  baseIncluded = signal<BaseItem[]>([]);
  modules = signal<CatalogItem[]>([]);
  complements = signal<CatalogItem[]>([]);

  // Paleta de fallback (en orden) para piezas sin coincidencia conocida.
  private readonly palette = [
    'var(--ts-mod-plataforma)',
    'var(--ts-mod-facturacion)',
    'var(--ts-mod-crm)',
    'var(--ts-mod-proyectos)',
    'var(--ts-mod-gastos)',
    'var(--ts-mod-fichaje)',
    'var(--ts-mod-tpv)',
    'var(--ts-mod-normativa)',
  ];

  private readonly colorByKeyword: [RegExp, string][] = [
    [/factur|verifactu/i, 'var(--ts-mod-facturacion)'],
    [/cliente|crm/i, 'var(--ts-mod-crm)'],
    [/proyecto|tarea|kanban/i, 'var(--ts-mod-proyectos)'],
    [/gasto|ia|escaneo|ocr/i, 'var(--ts-mod-gastos)'],
    [/fichaj|rrhh|turno|emplea/i, 'var(--ts-mod-fichaje)'],
    [/tpv|comand|restau|venta|inventario|producto|stock/i, 'var(--ts-mod-tpv)'],
    [/normativa|ticketbai|fiscal|calendario/i, 'var(--ts-mod-normativa)'],
    [/plataforma|documento|archivo|contenido|cms|web|usuario|portal/i, 'var(--ts-mod-plataforma)'],
  ];

  constructor() {
    effect(() => {
      const lang = this.langService.currentLang();
      void this.load(lang);
    });
  }

  private async load(lang: string): Promise<void> {
    this.isLoading.set(true);
    const groups = await this.saas.getModulesByCategory(lang);

    const base: BaseItem[] = [];
    const mods: CatalogItem[] = [];
    const comps: CatalogItem[] = [];
    let i = 0;

    for (const group of groups) {
      // Packs de sector: se muestran en /precios, no en el catálogo de la home.
      if (VERTICAL_CATEGORY_RE.test(group.category)) continue;
      if (CORE_CATEGORY_RE.test(group.category)) {
        for (const item of group.items) {
          base.push({ label: item.title, color: this.colorFor(item, i++) });
        }
        continue;
      }
      // Resto: por precio → módulo (>= 8) o complemento (por debajo).
      for (const item of group.items) {
        const priceNum = parseFloat(item.price) || 0;
        if (priceNum >= MODULE_PRICE) mods.push(this.toCard(item, i++));
        else if (priceNum > 0) comps.push(this.toCard(item, i++));
      }
    }

    this.baseIncluded.set(base);
    this.modules.set(mods);
    this.complements.set(comps);
    this.isLoading.set(false);
  }

  private toCard(item: CmsProductItem, index: number): CatalogItem {
    return {
      title: item.title,
      desc: item.subtitle,
      price: item.price,
      unit: item.priceUnit || '€/mes',
      color: this.colorFor(item, index),
      capacity:
        item.unitsIncluded && item.unitsIncluded > 0
          ? `Incluye ${item.unitsIncluded} ${item.unitLabel || 'usos al mes'}`
          : null,
    };
  }

  private colorFor(item: CmsProductItem, index: number): string {
    const haystack = `${item.title} ${item.category}`;
    for (const [re, color] of this.colorByKeyword) {
      if (re.test(haystack)) return color;
    }
    return this.palette[index % this.palette.length];
  }
}
